import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtINR2, fmtAmount } from "@/lib/stayflowApi";

const LINE_FALLBACK = { room: "Room", extra_adult: "Extra adult", child_7_17: "Child 7–17" };

// Every figure here comes from the server's quote — nothing is calculated in the browser.
export const PriceBreakdown = ({ quote, nights, loading, error, idPrefix = "" }) => {
  if (nights <= 0) return null;
  if (error) {
    return (
      <p className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700" data-testid={`${idPrefix}price-error`}>{error}</p>
    );
  }
  if (!quote || loading) {
    return (
      <div className="space-y-2.5" data-testid={`${idPrefix}price-skeleton`}>
        <Skeleton className="h-4 w-2/3 bg-paper-muted" />
        <Skeleton className="h-4 w-1/2 bg-paper-muted" />
        <Skeleton className="h-6 w-full bg-paper-muted" />
      </div>
    );
  }
  const q = quote;
  const fees = Number(q.guest_fees || 0);
  const qNights = q.nights || nights;
  const lines = Array.isArray(q.price_lines) && q.price_lines.length
    ? q.price_lines
    : [{ code: "room", label: `Room × ${qNights} night${qNights !== 1 ? "s" : ""}`, amount: q.room_total }];
  return (
    <div className="space-y-2.5 text-sm" data-testid={`${idPrefix}price-breakdown`}>
      {lines.map((l, i) => (
        <div key={`${l.code || i}`} className="flex justify-between text-ink-soft">
          <span>{l.label || LINE_FALLBACK[l.code] || "Stay"}</span>
          <span data-testid={`${idPrefix}price-line-${l.code || i}`}>{fmtINR2(l.amount)}</span>
        </div>
      ))}
      <div className="flex justify-between text-ink-soft">
        <span>GST {q.room_gst_percent}%</span>
        <span data-testid={`${idPrefix}price-gst`}>{fmtINR2(q.room_gst_amount)}</span>
      </div>
      {fees > 0 && (
        <div className="flex justify-between text-ink-soft">
          <span>Service fee</span>
          <span data-testid={`${idPrefix}price-fees`}>{fmtINR2(fees)}</span>
        </div>
      )}
      <div className="border-t border-black/10 pt-2.5 flex justify-between items-baseline">
        <span className="font-heading font-bold text-ink">Total</span>
        <span className="font-heading font-extrabold text-lg text-ink" data-testid={`${idPrefix}price-total`}>{fmtAmount(q.amount_payable)}</span>
      </div>
    </div>
  );
};

export default PriceBreakdown;
